// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useGetPrepareHoldings } from '@hooks/other/useGetPrepareHoldings';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { Typography } from '@mui/material';
import { Holding } from '@daml.js/dtc-registry-holding/lib/Dtc/Registry/Holding/V3/Holding';
import FormDialog from '../../../components/Dialog/FormDialog';
import { ExecuteBefore } from '../../../components/ExecuteBefore';
import TextInput from '../../../components/Form/TextInput';
import { useParties } from '../../../contexts/PartiesContext';
import { useInstrumentConfigurationsById } from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import TemplateContract from '../../../utils/TemplateContract';
import { getValidateAmountMessage } from '../../../utils/getValidateAmountMessage';
import { MaxLimitErrorMessage } from './MaxLimitErrorMessage';
import { useOfferTransferInstruction } from './useOfferTransferInstruction';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  holding?: TemplateContract<Holding>;
  disableDialog?: boolean;
};

export const OfferTransferDialog = ({ disableDialog, open, onClose, holding }: DialogProps) => {
  const [receiver, setReceiver] = useState<string>('');
  const [tokenId, setTokenId] = useState<string>(holding?.payload.instrument.id || '');
  const [amount, setAmount] = useState<string>(holding?.payload.amount || '');
  const [senderLabel, setSenderLabel] = useState<string>(holding?.payload.label || '');
  const [reference, setReference] = useState<string>('');
  const [amountValidationMsg, setAmountValidationMsg] = useState('');
  const prepareHoldings = useGetPrepareHoldings();
  const { party } = useParties();
  const [hasDateError, setDateError] = useState(false);
  const defaultExecuteBefore = dayjs().add(3, 'day');
  const [executeBefore, setExecuteBefore] = useState(defaultExecuteBefore);
  const instruments = useInstrumentConfigurationsById();

  const onDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) {
      return;
    }
    setDateError(false);
    const isBefore = date.isBefore(dayjs());
    if (isBefore) {
      setDateError(true);
    }
    const now = dayjs();
    const dateWithCurrTime = date
      .set('hour', now.hour())
      .set('minute', now.minute())
      .set('second', now.second());
    setExecuteBefore(dateWithCurrTime);
  };
  const handlePresetDays = (days: number) => {
    setDateError(false);
    const newDate = dayjs().add(days, 'day');
    setExecuteBefore(newDate);
  };
  const [showMaxLimitError, setShowMaxLimitError] = useState(false);
  const offTransferInstruction = useOfferTransferInstruction();
  const onTransferTransferInstruction = async () => {
    const instrument = instruments[tokenId];

    if (!instrument) {
      // This should never happen, since the button is disabled if the instrument
      // cannot be found among the known configurations.
      throw Error(`Cannot find instrument configuration for token: ${tokenId}`);
    }

    setShowMaxLimitError(false);

    const { holdingCids, exceedsMaxLimit } = prepareHoldings({
      instrumentId: instrument.payload.defaultIdentifier.id,
      registrar: instrument.payload.registrar,
      amount,
      owner: party,
    });

    if (exceedsMaxLimit) {
      setShowMaxLimitError(true);
      return;
    }

    console.log('>>>>', {
      party,
      receiver,
      amount,
      source: instrument.payload.registrar,
      instrumentId: instrument.payload.defaultIdentifier.id,
      holdingCids,
      reference,
      executeBefore: executeBefore.toISOString(),
    });

    await offTransferInstruction({
      party,
      receiver,
      amount,
      source: instrument.payload.registrar,
      instrumentId: instrument.payload.defaultIdentifier.id,
      holdingCids,
      reference,
      executeBefore: executeBefore.toISOString(),
    });
    onClose();
  };

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const getTransferValidationMsg = getValidateAmountMessage('Transfer', holding?.payload.amount);

  const disableSubmit =
    !receiver ||
    !amount ||
    hasDateError ||
    !!amountValidationMsg.length ||
    !tokenId ||
    !instruments[tokenId];
  return (
    <FormDialog
      disableDialog={disableDialog}
      open={open}
      title="Offer Transfer"
      onCancel={handleClose}
      onSubmit={onTransferTransferInstruction}
      submitName="Offer"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput
        enableTopLabel
        label="Instrument ID"
        value={tokenId}
        setValue={setTokenId}
        required
      />
      <TextInput enableTopLabel label="Receiver" value={receiver} setValue={setReceiver} required />

      <TextInput
        label="Amount"
        getValidationMessage={getTransferValidationMsg}
        setValidationMessage={setAmountValidationMsg}
        validationMessage={amountValidationMsg}
        value={amount}
        setValue={setAmount}
        required
        type="number"
      />
      <Typography color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        Execute Before
      </Typography>
      <ExecuteBefore
        handlePresetDays={handlePresetDays}
        selectedDate={executeBefore}
        onChange={onDateChange}
      />
      {hasDateError && (
        <Typography variant="caption" color="error">
          Date must be in the future
        </Typography>
      )}
      <TextInput
        label="Label"
        value={senderLabel}
        setValue={setSenderLabel}
        disabled={!!holding}
        hidden
      />

      <TextInput label="Reference" value={reference} setValue={setReference} />
      {showMaxLimitError && <MaxLimitErrorMessage />}
    </FormDialog>
  );
};
